import _typeof from "@babel/runtime/helpers/typeof";
import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import { Field as FormField } from 'react-final-form';
import { Box, Typography, TextField } from '@mui/material';
import { FIELD_DESCRIPTION, FIELD_TITLE, FIELD_TEXT, FIELD_TEXTAREA, FIELDS_INPUT_DECISION_RADIOGROUP, FIELDS_START_END_INPUTS } from 'constants/fields';
import DecisionRadioGroup from 'UI/Inputs/DecisionRadioGroup';
import StartEndInputs from 'UI/Inputs/StartEndInputs';
import './FormFieldRenderer.scss';
var FormFieldRenderer = function FormFieldRenderer(_ref) {
  var field = _ref.field,
    index = _ref.index,
    translate = _ref.translate;
  var renderTitle = function renderTitle() {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "subtitle2",
      sx: {
        fontWeight: 600
      }
    }, translate ? translate(field.value) || field.value : field.value);
  };
  var renderDescription = function renderDescription() {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary"
    }, translate ? translate(field.value) || field.value : field.value);
  };
  var renderText = function renderText() {
    var isRequired = field.required !== false; // Default to required unless explicitly set to false

    return /*#__PURE__*/React.createElement(Box, null, field.label && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: {
        mb: 0.5,
        fontWeight: 500
      }
    }, translate ? translate(field.label) || field.label : field.label, isRequired && /*#__PURE__*/React.createElement("span", {
      style: {
        color: 'red'
      }
    }, " *")), /*#__PURE__*/React.createElement(FormField, {
      name: field.name || "field-".concat(index),
      validate: isRequired ? function (value) {
        return value ? undefined : translate('This field is required') || 'This field is required';
      } : undefined,
      render: function render(_ref2) {
        var input = _ref2.input,
          meta = _ref2.meta;
        return /*#__PURE__*/React.createElement(TextField, _extends({}, input, {
          type: "text",
          placeholder: translate ? translate(field.placeholder) || field.placeholder : field.placeholder,
          disabled: field.disabled || false,
          fullWidth: true,
          variant: "outlined",
          size: "small",
          error: meta.submitFailed && meta.error,
          helperText: meta.submitFailed && meta.error ? meta.error : ''
        }));
      }
    }));
  };
  var renderTextarea = function renderTextarea() {
    var isRequired = field.required !== false; // Default to required unless explicitly set to false

    return /*#__PURE__*/React.createElement(Box, null, field.label && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: {
        mb: 0.5,
        fontWeight: 500
      }
    }, translate ? translate(field.label) || field.label : field.label, isRequired && /*#__PURE__*/React.createElement("span", {
      style: {
        color: 'red'
      }
    }, " *")), /*#__PURE__*/React.createElement(FormField, {
      name: field.name || "field-".concat(index),
      validate: isRequired ? function (value) {
        return value ? undefined : translate('This field is required') || 'This field is required';
      } : undefined,
      render: function render(_ref3) {
        var input = _ref3.input,
          meta = _ref3.meta;
        return /*#__PURE__*/React.createElement(TextField, _extends({}, input, {
          multiline: true,
          rows: 4,
          placeholder: translate ? translate(field.placeholder) || field.placeholder : field.placeholder,
          disabled: field.disabled || false,
          fullWidth: true,
          variant: "outlined",
          size: "small",
          error: meta.submitFailed && meta.error,
          helperText: meta.submitFailed && meta.error ? meta.error : ''
        }));
      }
    }));
  };
  var renderDecisionRadioGroup = function renderDecisionRadioGroup() {
    return /*#__PURE__*/React.createElement(Box, null, field.label && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: {
        mb: 0.5,
        fontWeight: 500
      }
    }, translate ? translate(field.label) || field.label : field.label), /*#__PURE__*/React.createElement(FormField, {
      name: field.name || "decision-".concat(index),
      render: function render(_ref4) {
        var input = _ref4.input,
          meta = _ref4.meta;
        var labels = [translate ? translate('Approved') || 'Approved' : 'Approved', translate ? translate('Denied') || 'Denied' : 'Denied'];
        try {
          var parsed = JSON.parse(field.placeholder || '[]');
          if (Array.isArray(parsed)) labels = parsed;
        } catch (e) {}
        return /*#__PURE__*/React.createElement(DecisionRadioGroup, {
          input: input,
          meta: meta,
          name: input ? input.name : field.name || "decision-".concat(index),
          onChange: input ? input.onChange : undefined,
          disabled: field.disabled || false,
          labels: labels,
          translate: translate
        });
      }
    }));
  };
  var renderStartEndInputs = function renderStartEndInputs() {
    var isRequired = field.required !== false; // Default to required unless explicitly set to false
    var startEndConfig = {};
    try {
      var parsedConfig = JSON.parse(field.placeholder || '{}');
      if (parsedConfig && !Array.isArray(parsedConfig) && _typeof(parsedConfig) === 'object') {
        startEndConfig = parsedConfig;
      }
    } catch (e) {
      // Keep default config when placeholder is not JSON
    }
    var includeTime = !!(startEndConfig.includeTime || startEndConfig.withTime || startEndConfig.showTime || field.includeTime);
    return /*#__PURE__*/React.createElement(Box, null, field.label && /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      sx: {
        mb: 0.5,
        fontWeight: 500
      }
    }, translate ? translate(field.label) || field.label : field.label, isRequired && /*#__PURE__*/React.createElement("span", {
      style: {
        color: 'red'
      }
    }, " *")), /*#__PURE__*/React.createElement(FormField, {
      name: field.name || "startend-".concat(index),
      validate: isRequired ? function (value) {
        if (!value) return translate ? translate('This field is required') || 'This field is required' : 'This field is required';
        try {
          var parsed = typeof value === 'string' ? JSON.parse(value) : value;
          if (!parsed.start || !parsed.end) {
            return translate ? translate('Both start and end dates are required') || 'Both start and end dates are required' : 'Both start and end dates are required';
          }
        } catch (e) {
          return translate ? translate('Invalid date range') || 'Invalid date range' : 'Invalid date range';
        }
        return undefined;
      } : undefined,
      render: function render(_ref5) {
        var input = _ref5.input,
          meta = _ref5.meta;
        var parsed = {
          start: '',
          end: ''
        };

        // If there's an existing value stored in the form field, parse it
        // (final-form stores the value as a JSON string in this implementation).
        try {
          var raw = input && input.value;
          var existing = typeof raw === 'string' && raw ? JSON.parse(raw) : raw;
          if (existing) {
            parsed.start = existing.start || '';
            parsed.end = existing.end || '';
          }
        } catch (e) {
          // keep defaults if parsing fails
        }
        parsed.labels = {
          startLabel: translate ? translate('Start') || 'Start' : 'Start',
          endLabel: translate ? translate('End') || 'End' : 'End'
        };
        var handleChange = function handleChange(next) {
          var payload = JSON.stringify({
            start: next.start || '',
            end: next.end || ''
          });
          if (input && input.onChange) input.onChange(payload);
        };
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StartEndInputs, {
          input: {
            name: input && input.name,
            value: {
              start: parsed.start,
              end: parsed.end
            },
            onChange: handleChange
          },
          disabled: field.disabled || false,
          labels: parsed.labels,
          includeTime: includeTime,
          translate: translate,
          error: meta.submitFailed && meta.error
        }), meta.submitFailed && meta.error && /*#__PURE__*/React.createElement(Typography, {
          variant: "caption",
          color: "error",
          sx: {
            mt: 0.5,
            display: 'block'
          }
        }, meta.error));
      }
    }));
  };
  var renderInput = function renderInput() {
    var type = field.type.toLowerCase();
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        p: 1.5,
        border: '1px solid #ddd',
        borderRadius: 1,
        bgcolor: '#f9f9f9'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary",
      sx: {
        fontStyle: 'italic'
      }
    }, translate(type)));
  };

  // Determine which renderer to use based on field type
  if (field.type === FIELD_TITLE) {
    return /*#__PURE__*/React.createElement(Box, {
      key: "field-".concat(index)
    }, renderTitle());
  }
  if (field.type === FIELD_DESCRIPTION) {
    return /*#__PURE__*/React.createElement(Box, {
      key: "field-".concat(index)
    }, renderDescription());
  }
  if (field.type === FIELD_TEXT) {
    return /*#__PURE__*/React.createElement(Box, {
      key: "field-".concat(index)
    }, renderText());
  }
  if (field.type === FIELD_TEXTAREA) {
    return /*#__PURE__*/React.createElement(Box, {
      key: "field-".concat(index)
    }, renderTextarea());
  }
  if (field.type === FIELDS_INPUT_DECISION_RADIOGROUP) {
    return /*#__PURE__*/React.createElement(Box, {
      key: "field-".concat(index)
    }, renderDecisionRadioGroup());
  }
  if (field.type === FIELDS_START_END_INPUTS) {
    return /*#__PURE__*/React.createElement(Box, {
      key: index
    }, renderStartEndInputs());
  }

  // Default: render generic input
  return /*#__PURE__*/React.createElement(Box, {
    key: "field-".concat(index)
  }, renderInput());
};
export default FormFieldRenderer;